import { toast, ToastOptions } from "react-toastify";

// Default toast options
const defaultOptions: ToastOptions = {
  position: "bottom-right",
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  draggable: true,
};

const nestedBullets = (obj: any): React.ReactNode => {
  let content = obj;

  const isIterable = obj && (typeof obj === "object" || Array.isArray(obj));

  if (Array.isArray(obj)) {
    content = obj.map((item, index) => (
      isIterable ? nestedBullets(item) : item?.toString()
    ));
  }
  else if (typeof obj === "object") {
    content = Object.entries(obj).map(([key, value]) => (
        <li key={key}>
          <strong>{key}:</strong> {isIterable ? nestedBullets(value) : value?.toString()}
        </li>
    ))
  }

  return (
    <ul className="list-disc pl-2">
      {content}
    </ul>
  );
}

// Utility functions for different types of toasts
export const toastUtils = {
  // Success toast
  success: (message: string, options?: ToastOptions) => {
    toast.success(message, { ...defaultOptions, ...options });
  },

  // Error toast
  error: (message: string, options?: ToastOptions) => {
    toast.error(message, { ...defaultOptions, ...options });
  },

  // opinionated toast for API errors from DRF
  apiError: (prefix: string, error: any, options?: ToastOptions) => {
    // api errors will typically have a 'cause' property structured as { [field_name]: [error_message], ... }
    // we want to display this as a bulleted list
    // if there is no cause, we will just coerce the error object to a string and display that
    const cause = error.cause;
    let message: any = prefix;

    // create a JSX object to display the list of errors
    if (cause && typeof cause === "object") {
      message = (
        <div>
          <div>{prefix}:</div>
          {/* {Object.entries(cause).map(([field, msg]) => (
            <div key={field}>
              <strong>&bull; {field}:</strong> {msg?.toString()}
            </div>
          ))} */}
          <div className="ml-2">{ nestedBullets(cause) }</div>
        </div>
      );
    }
    else {
      message = `${prefix}: ${error.toString()}`;
    }

    toast.error(message, { ...defaultOptions, ...options });
  },

  // Info toast
  info: (message: string, options?: ToastOptions) => {
    toast.info(message, { ...defaultOptions, ...options });
  },

  // Warning toast
  warning: (message: string, options?: ToastOptions) => {
    toast.warning(message, { ...defaultOptions, ...options });
  },

  // Default toast
  default: (message: string, options?: ToastOptions) => {
    toast(message, { ...defaultOptions, ...options });
  },

  // Promise toast - useful for async operations
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      pending: string;
      success: string;
      error: string;
    },
    options?: ToastOptions,
  ) => {
    return toast.promise(promise, messages, { ...defaultOptions, ...options });
  },

  // Dismiss all toasts
  dismiss: () => {
    toast.dismiss();
  },

  // Custom toast with JSX content
  custom: (content: React.ReactNode, options?: ToastOptions) => {
    toast(content, { ...defaultOptions, ...options });
  },
};

// Export individual functions for convenience
export const { success, error, info, warning, promise, dismiss, custom } =
  toastUtils;

export default toastUtils;
